/**
 * Interactive tool widgets, isolated from the shared `pages.tsx` barrel.
 *
 * The five client widgets (and their logic libs: model-pricing, automation-roi,
 * coding-tool-quiz, ...) are ONLY ever mounted on `/tools/$toolSlug`. When their
 * imports lived at the top of `pages.tsx` - the module every route imports for
 * its page component - they collapsed into the single shared first-party chunk
 * and shipped to EVERY route (home, courses, lessons, ...) that never renders a
 * tool. Moving them here, imported directly by the tool route files (en + de)
 * and handed to `ToolPage` as a render prop, keeps them in the tool route's own
 * chunk so the rest of the site no longer downloads or parses them. Behaviour
 * and SSR output are identical (the route still imports the widget eagerly, so
 * it server-renders exactly as before - this only relocates the bytes off the
 * shared barrel).
 */
import { useRef } from 'react'
import type { ReactNode } from 'react'
import { trackEvent } from '../lib/analytics'
import type { Tool } from '../content/catalog'
import { LlmsTxtGenerator } from './tools/llms-txt-generator'
import { LlmCostCalculator } from './tools/llm-cost-calculator'
import { TokenContextEstimator } from './tools/token-context-estimator'
import { AutomationRoiCalculator } from './tools/automation-roi-calculator'
import { CodingToolQuiz } from './tools/coding-tool-quiz'

/**
 * Map a tool's `toolKey` discriminant to its client widget. Add a member to
 * {@link Tool['toolKey']} and an entry here to ship a new interactive tool; the
 * `Record<ToolKey, ...>` type makes a missing widget a compile error.
 */
const TOOL_WIDGETS: Record<Tool['toolKey'], () => ReactNode> = {
  'llms-txt-generator': () => <LlmsTxtGenerator />,
  'llm-cost-calculator': () => <LlmCostCalculator />,
  'token-context-estimator': () => <TokenContextEstimator />,
  'ai-automation-roi-calculator': () => <AutomationRoiCalculator />,
  'ai-coding-tool-quiz': () => <CodingToolQuiz />,
}

/**
 * Mount the correct client widget for a tool by its discriminant. The wrapper
 * reports `tool_used` once per page view, on the first real input or click
 * inside the widget, so every tool is measured without per-tool code.
 */
export function ToolWidget({ tool }: { tool: Tool }) {
  const Widget = TOOL_WIDGETS[tool.toolKey]
  const reported = useRef(false)
  const report = (interaction: 'input' | 'click') => {
    if (reported.current) return
    reported.current = true
    trackEvent('tool_used', { tool: tool.toolKey, interaction })
  }
  return (
    <div
      className="tool-widget-root"
      onInputCapture={() => report('input')}
      onClickCapture={() => report('click')}
    >
      {Widget()}
    </div>
  )
}
